import type {RoleRecord} from './rolePermissionRequests'

export type RolePermissionActorContext = {
  isSuperAdmin: boolean
  actorRoleId?: number | null
  actorCompanyGroupId?: number | null
  actorCompanyAdminId?: number | null
  actorEffectiveBehavior?: string | null
  hasUpdateRolePermission: boolean
}

const AUTHORITY_RANK: Record<string, number> = {
  SUPER_ADMIN: 1000,
  COMPANY_GROUP_ADMIN: 800,
  COMPANY_ADMIN: 700,
  OPERATOR: 500,
  SUPERINTENDENT: 500,
  SHORE: 400,
  CREW: 300,
  VENDOR: 200,
  CUSTOM: 100,
}

const TENANT_EDITABLE_SHARED_BEHAVIORS = new Set([
  'COMPANY_ADMIN',
  'OPERATOR',
  'SUPERINTENDENT',
  'SHORE',
  'CREW',
])

const VENDOR_EXTERNAL_BEHAVIORS = new Set([
  'VENDOR',
  'VENDOR_USER',
  'SUPPLIER',
  'COMPANY_OWNER',
])

export function resolveActorCompanyGroupId(currentUser: Record<string, unknown> | null | undefined): number | null {
  if (!currentUser) return null
  const candidates = [
    currentUser.companyGroupAdminId,
    (currentUser.companyGroupAdmin as {id?: number} | undefined)?.id,
    (currentUser.cgaid as {id?: number} | undefined)?.id,
    (currentUser.vessel as Record<string, unknown> | undefined)?.companyGroupAdminId,
    ((currentUser.vessel as Record<string, unknown> | undefined)?.companyGroupAdmin as {id?: number} | undefined)?.id,
    (currentUser.companyAdmin as {cgaid?: {id?: number}} | undefined)?.cgaid?.id,
    (currentUser.caid as {cgaid?: {id?: number}} | undefined)?.cgaid?.id,
  ]
  for (const candidate of candidates) {
    const n = Number(candidate)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

export function resolveActorCompanyAdminId(currentUser: Record<string, unknown> | null | undefined): number | null {
  if (!currentUser) return null
  const candidates = [
    currentUser.companyAdminId,
    (currentUser.companyAdmin as {id?: number} | undefined)?.id,
    (currentUser.caid as {id?: number} | undefined)?.id,
    (currentUser.vessel as Record<string, unknown> | undefined)?.companyAdminId,
    ((currentUser.vessel as Record<string, unknown> | undefined)?.companyAdmin as {id?: number} | undefined)?.id,
  ]
  for (const candidate of candidates) {
    const n = Number(candidate)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

function roleBehavior(role: RoleRecord | null | undefined): string {
  return (role?.effectiveBehaviorType || role?.baseRoleType || role?.roleType || 'CUSTOM').trim().toUpperCase()
}

function roleAuthorityRank(role: RoleRecord): number {
  return AUTHORITY_RANK[roleBehavior(role)] ?? AUTHORITY_RANK.CUSTOM
}

function actorAuthorityRank(actor: RolePermissionActorContext): number {
  const behavior = (actor.actorEffectiveBehavior || 'CUSTOM').trim().toUpperCase()
  return AUTHORITY_RANK[behavior] ?? AUTHORITY_RANK.CUSTOM
}

export function isVendorExternalRole(role: RoleRecord | null | undefined): boolean {
  return VENDOR_EXTERNAL_BEHAVIORS.has(roleBehavior(role))
}

export function isProtectedSuperAdminRole(role: RoleRecord | null | undefined): boolean {
  if (!role) return true
  const behavior = roleBehavior(role)
  if (behavior === 'SUPER_ADMIN') return true
  if (role.assignable === false && !role.customRole) return true
  return false
}

export function canManageRolePermissions(
  role: RoleRecord | null | undefined,
  actor: RolePermissionActorContext
): boolean {
  if (!role || !actor.hasUpdateRolePermission) return false
  if (isProtectedSuperAdminRole(role) || isVendorExternalRole(role)) return false
  if (actor.actorRoleId != null && role.id === actor.actorRoleId) return false
  if (actor.isSuperAdmin) return true

  const actorBehavior = (actor.actorEffectiveBehavior || '').trim().toUpperCase()
  if (actorBehavior !== 'COMPANY_GROUP_ADMIN') return false
  if (actorAuthorityRank(actor) <= roleAuthorityRank(role)) return false

  if (!role.customRole) {
    return TENANT_EDITABLE_SHARED_BEHAVIORS.has(roleBehavior(role))
  }

  if (role.companyGroupAdminId == null) return false
  return actor.actorCompanyGroupId != null && Number(role.companyGroupAdminId) === Number(actor.actorCompanyGroupId)
}

export function rolePermissionManageBlockedReason(
  role: RoleRecord | null | undefined,
  actor: RolePermissionActorContext
): string {
  if (!role) return 'Select a role to manage permissions.'
  if (!actor.hasUpdateRolePermission) return 'You do not have permission to update role permissions.'
  if (isProtectedSuperAdminRole(role)) return 'Protected role - permissions are read-only.'
  if (isVendorExternalRole(role)) return 'Vendor roles cannot be managed here.'
  if (actor.actorRoleId != null && role.id === actor.actorRoleId) {
    return 'Company cannot modify its own role permissions.'
  }
  if (actor.isSuperAdmin) {
    return 'You do not have permission to change this role\'s permissions.'
  }

  const actorBehavior = (actor.actorEffectiveBehavior || '').trim().toUpperCase()
  if (actorBehavior !== 'COMPANY_GROUP_ADMIN') {
    return 'Only superadmin or company group admin may manage role permissions.'
  }
  if (actorAuthorityRank(actor) <= roleAuthorityRank(role)) {
    return 'You can manage only lower roles within your company group.'
  }
  if (role.customRole) {
    if (role.companyGroupAdminId == null) return 'Only a super administrator can manage global custom roles.'
    if (actor.actorCompanyGroupId == null || Number(role.companyGroupAdminId) !== Number(actor.actorCompanyGroupId)) {
      return 'This role belongs to another company group.'
    }
    return 'You do not have permission to change this role\'s permissions.'
  }
  if (!TENANT_EDITABLE_SHARED_BEHAVIORS.has(roleBehavior(role))) {
    return 'You can manage only lower roles within your company group.'
  }
  return 'You do not have permission to change this role\'s permissions.'
}
